import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, PencilSimple, Trash, ArrowsClockwise, Star, X, WarningCircle, ShareNetwork, Eye, EyeSlash } from "@phosphor-icons/react";
import PotgShareModal from "@/components/PotgShareModal";
import { useSettings } from "@/context/SettingsContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export default function PlayerOfGameAdmin() {
  const { settings, reloadSettings } = useSettings();
  const [cards, setCards] = useState([]);
  const [statsByGame, setStatsByGame] = useState({});
  const [gamesById, setGamesById] = useState({});
  const [manage, setManage] = useState(null);
  const [share, setShare] = useState(null);
  const [reason, setReason] = useState("");
  const [replacePid, setReplacePid] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingWeek, setSavingWeek] = useState(null);

  const load = () => {
    api.get("/potg").then((r) => setCards(r.data || [])).catch(() => {});
    api.get("/games").then((r) => {
      const map = {};
      const gmap = {};
      (r.data || []).forEach((g) => { map[g.id] = (g.stats || []).filter((s) => s.player_id); gmap[g.id] = g; });
      setStatsByGame(map); setGamesById(gmap);
    }).catch(() => {});
  };
  useEffect(load, []);

  const openManage = (c) => { setManage(c); setReason(""); setReplacePid(c.player_id); };
  const close = () => setManage(null);

  const visibleWeeks = settings?.potg_visible_weeks || [];
  const weeksWithAwards = useMemo(() => {
    const counts = new Map();
    cards.forEach((c) => { if (c.week != null) counts.set(c.week, (counts.get(c.week) || 0) + 1); });
    return Array.from(counts.entries()).map(([week, count]) => ({ week, count })).sort((a, b) => b.week - a.week);
  }, [cards]);
  const latestWeek = weeksWithAwards.length ? weeksWithAwards[0].week : null;

  const toggleWeek = async (week, makeVisible) => {
    const set = new Set(visibleWeeks);
    if (makeVisible) set.add(week); else set.delete(week);
    setSavingWeek(week);
    try {
      await api.put("/settings", { potg_visible_weeks: Array.from(set) });
      await reloadSettings();
      toast.success(makeVisible ? `Week ${week} is now visible to the public` : `Week ${week} hidden from the public`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingWeek(null); }
  };

  const doOverride = async () => {
    if (!reason.trim()) { toast.error("Enter a reason for the change"); return; }
    if (!replacePid) { toast.error("Select a player"); return; }
    setBusy(true);
    try {
      await api.put(`/potg/${manage.game_id}`, { player_id: replacePid, reason: reason.trim() });
      toast.success("Player of the Game updated"); close(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRecalc = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/potg/${manage.game_id}/recalc`, { reason: reason.trim() });
      toast.success(`Recalculated → ${r.data.player_of_game_name || "no pick"}`); close(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!reason.trim()) { toast.error("A reason is required to delete the award"); return; }
    if (!window.confirm(`Delete the Player of the Game award for this game? This cannot be undone (you can re-run Recalculate later).`)) return;
    setBusy(true);
    try {
      await api.delete(`/potg/${manage.game_id}?reason=${encodeURIComponent(reason.trim())}`);
      toast.success("Award cleared"); close(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const boxPlayers = (() => {
    if (!manage) return [];
    const g = gamesById[manage.game_id];
    const all = statsByGame[manage.game_id] || [];
    if (!g) return all;
    const hs = Number(g.home_score || 0), as = Number(g.away_score || 0);
    if (hs === as) return []; // tie / unfinished — no eligible winner
    const winId = hs > as ? g.home_team_id : g.away_team_id;
    const winSide = hs > as ? "home" : "away";
    return all.filter((s) => (s.team_id ? s.team_id === winId : s.side === winSide));
  })();

  return (
    <div className="mt-12 pt-8 border-t border-white/10" data-testid="potg-admin">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={22} weight="fill" className="text-[#FF5722]" />
        <h2 className="font-display text-2xl uppercase">Player of the Game</h2>
      </div>
      <p className="text-white/40 text-sm mb-6">Auto-published when a game is finalized — chosen only from the <span className="text-white/70 font-semibold">winning team</span> (Points×1 + Assists×2 + Rebounds×1.5 + Blocks×2). Override, recalculate, or clear any award below — every change is logged.</p>

      {/* Public visibility by week */}
      {weeksWithAwards.length > 0 && (
        <div className="border border-white/10 bg-[#0F0F0F] p-5 mb-8" data-testid="potg-week-visibility">
          <div className="flex items-center gap-2 mb-1">
            <Eye size={18} className="text-[#FF5722]" />
            <h3 className="font-display text-lg uppercase">Public Visibility by Week</h3>
          </div>
          <p className="text-white/40 text-xs mb-4">The public Awards page shows only the <span className="text-white/70 font-semibold">latest week</span> by default. Unhide any earlier week to make it public too.</p>
          <div className="flex flex-wrap gap-2">
            {weeksWithAwards.map(({ week, count }) => {
              const isLatest = week === latestWeek;
              const visible = isLatest || visibleWeeks.includes(week);
              return (
                <button
                  key={week}
                  disabled={isLatest || savingWeek === week}
                  onClick={() => toggleWeek(week, !visible)}
                  data-testid={`potg-week-toggle-${week}`}
                  title={isLatest ? "Latest week is always public" : visible ? "Click to hide from public" : "Click to show on public page"}
                  className={`flex items-center gap-2 px-4 py-2.5 border text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
                    visible ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/10" : "border-white/15 text-white/40 hover:text-white/70"
                  } ${isLatest ? "cursor-default" : ""} disabled:opacity-60`}
                >
                  {visible ? <Eye size={16} weight="fill" /> : <EyeSlash size={16} />}
                  Week {week}
                  <span className="text-[10px] font-normal opacity-60">{count} POTG</span>
                  {isLatest && <span className="text-[9px] bg-[#FF5722] text-black px-1.5 py-0.5 rounded-sm">LATEST</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="border border-dashed border-white/15 py-12 text-center" data-testid="potg-admin-empty">
          <Star size={34} className="text-white/30 mx-auto mb-2" />
          <p className="text-white/50">No awards yet. Finalize a game to auto-publish its Player of the Game.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cards.map((c) => (
            <div key={c.game_id} data-testid={`potg-admin-row-${c.game_id}`} className="flex items-center gap-4 border border-white/10 bg-[#121212] p-4">
              <div className="w-14 h-14 bg-[#0A0A0A] border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                {c.photo_url ? <img src={resolveMedia(c.photo_url)} alt={c.player_name} className="w-full h-full object-cover" /> : <Star size={20} className="text-white/20" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold uppercase truncate flex items-center gap-2">{c.player_name}
                  {c.needs_review && <span title="Tie — needs review" className="text-[#FFB300]"><WarningCircle size={16} weight="fill" /></span>}
                  <span className="text-white/40 font-normal text-xs">· {c.division}</span></p>
                <p className="text-xs text-white/50 truncate">{c.team_name}{c.opponent ? ` vs ${c.opponent}` : ""} · {c.week != null ? `Wk ${c.week} · ` : ""}{fmtDate(c.date)} · {c.final_score}</p>
                <p className="text-xs text-white/40 tabular-nums mt-0.5">{c.pts} PTS · {c.ast} AST · {c.reb} REB · {c.blk} BLK</p>
              </div>
              <button onClick={() => setShare(c)} data-testid={`potg-admin-share-${c.game_id}`} className="shrink-0 flex items-center gap-1.5 text-xs uppercase font-bold border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 px-3 py-2 transition-colors">
                <ShareNetwork size={15} /> Share
              </button>
              <button onClick={() => openManage(c)} data-testid={`potg-admin-manage-${c.game_id}`} className="shrink-0 flex items-center gap-1.5 text-xs uppercase font-bold border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 px-3 py-2 transition-colors">
                <PencilSimple size={15} /> Manage
              </button>
            </div>
          ))}
        </div>
      )}

      {manage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={close}>
          <div className="bg-[#121212] border border-white/10 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="potg-manage-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="font-display text-xl uppercase">Manage Award</h3>
              <button onClick={close} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-white/60">{manage.division} · {manage.week != null ? `Week ${manage.week} · ` : ""}{fmtDate(manage.date)} · {manage.final_score}</p>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Player of the Game <span className="text-white/40 normal-case font-normal">(winning team only)</span></label>
                <select value={replacePid} onChange={(e) => setReplacePid(e.target.value)} data-testid="potg-replace-select" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm">
                  {boxPlayers.length === 0 && <option value="">No eligible winning-team players</option>}
                  {boxPlayers.map((s) => (
                    <option key={s.player_id} value={s.player_id}>{s.name} — {s.pts || 0}p {s.ast || 0}a {s.reb || 0}r {s.blk || 0}b</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Reason (required for change/delete — logged)</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="potg-reason-input" placeholder="e.g. Corrected rebound count after review" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={doOverride} disabled={busy} data-testid="potg-override-btn" className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide text-sm py-2.5 transition-colors">Save Change</button>
                <button onClick={doRecalc} disabled={busy} data-testid="potg-recalc-btn" title="Auto-pick from box score" className="flex items-center justify-center gap-1.5 border border-white/20 hover:border-white text-white/80 font-bold uppercase tracking-wide text-sm px-3 py-2.5 transition-colors"><ArrowsClockwise size={16} /> Recalc</button>
                <button onClick={doDelete} disabled={busy} data-testid="potg-delete-btn" className="flex items-center justify-center gap-1.5 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 font-bold uppercase tracking-wide text-sm px-3 py-2.5 transition-colors"><Trash size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {share && <PotgShareModal card={share} onClose={() => setShare(null)} />}
    </div>
  );
}